import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type CookItem } from '../api/endpoints';
import { ApiException } from '../api/client';
import { RecipeCard } from '../components/RecipeCard';
import { Skeleton, ErrorState, EmptyState } from '../components/states';
import { COURSE_CHIPS } from '../lib/cookCourses';
import { track } from '../lib/analytics';

// «Что приготовить» — строгий подбор из холодильника. Чипы типа блюда (мультивыбор,
// пусто = «Любое»); рецепты группируются по missingCount: 0 = можно готовить сейчас,
// 1..2 = почти готово (докупить). Лента RecipeCard как CuisineScreen.
export function CookScreen() {
  const nav = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [items, setItems] = useState<CookItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    setItems(null);
    const courses = [...selected];
    void endpoints.cookFromPantry(courses.length ? courses : undefined).then(
      (r) => {
        setItems(r.items);
        track('cook_result', { count: r.items.length });
      },
      (e) => setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить'),
    );
  }, [selected]);

  useEffect(() => {
    // Разовая загрузка при смене набора чипов — намеренный fetch-in-effect (план без TanStack).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  function toggle(course: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(course)) next.delete(course);
      else next.add(course);
      return next;
    });
  }

  const ready = (items ?? []).filter((it) => it.missingCount === 0);
  // «Почти готово» — не хватает 1–2 продуктов (верхняя граница дублирует серверный порог).
  const almost = (items ?? []).filter((it) => it.missingCount >= 1 && it.missingCount <= 2);

  return (
    <div className="px-4 pt-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} aria-label="Назад" className="text-xl text-ink/60">
          ←
        </button>
        <h1 className="text-2xl font-bold text-ink">Что приготовить</h1>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {COURSE_CHIPS.map((c) => {
          const active = selected.has(c.course);
          return (
            <button
              key={c.course}
              onClick={() => toggle(c.course)}
              aria-pressed={active}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition active:scale-[0.97] ${
                active
                  ? 'bg-primary-600 text-white shadow-card'
                  : 'bg-white text-ink/70 shadow-sm ring-1 ring-inset ring-black/5'
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : items === null ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : ready.length === 0 && almost.length === 0 ? (
        <EmptyState title="Ничего не подобрали" hint="Добавьте ещё продукты в холодильник." />
      ) : (
        <div className="space-y-8">
          {ready.length > 0 && (
            <section aria-label="Можно приготовить сейчас">
              <h2 className="mb-3 text-[17px] font-bold text-ink">Можно приготовить сейчас</h2>
              <div className="stagger space-y-4">
                {ready.map((it) => (
                  <RecipeCard
                    key={it.recipe.id}
                    title={it.recipe.title ?? ''}
                    imageUrl={it.recipe.imageUrl}
                    cookTimeMin={it.recipe.cookTimeMin}
                    servings={it.recipe.servings}
                    onClick={() => nav(`/recipe/${it.recipe.id}`)}
                  />
                ))}
              </div>
            </section>
          )}
          {almost.length > 0 && (
            <section aria-label="Почти готово">
              <h2 className="mb-3 text-[17px] font-bold text-ink">Почти готово</h2>
              <div className="stagger space-y-4">
                {almost.map((it) => (
                  <RecipeCard
                    key={it.recipe.id}
                    title={it.recipe.title ?? ''}
                    imageUrl={it.recipe.imageUrl}
                    cookTimeMin={it.recipe.cookTimeMin}
                    servings={it.recipe.servings}
                    reason={`не хватает ${it.missingCount} шт.`}
                    onClick={() => nav(`/recipe/${it.recipe.id}`)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
